import type { AdminEvalJob } from "@repo/marketplace-admin"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type { ColDef, ICellRendererParams } from "ag-grid-community"
import { FlaskConical, RefreshCw } from "lucide-react"
import * as React from "react"
import { PageHeader, Toolbar } from "@/components/layout"
import { Button } from "@/components/ui/button"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { EvaluateDialog } from "@/features/card-scanner/eval-actions"
import { ScannerGrid } from "@/features/card-scanner/ScannerGrid"
import {
	EVAL_JOB_STATUSES,
	EVAL_JOB_TRIGGERS,
	formatDuration,
	HeartbeatCell,
	Instant,
	QueryError,
	roleLabel,
	StatusBadge,
	TERMINAL_JOB_STATUSES,
	useScannerGate,
} from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"

export const Route = createFileRoute("/card-scanner/eval-jobs")({
	component: EvalJobsPage,
})

const ALL = "__all__"

/** "Embedder v3 + Segmenter v4" for a job's targets. */
export function describeTargets(job: AdminEvalJob): string {
	return job.targetRoles
		.map((role) => {
			const ref = job.modelSet[role]
			return ref ? `${roleLabel(role)} v${ref.version}` : roleLabel(role)
		})
		.join(" + ")
}

function EvalJobsPage() {
	const allowed = useScannerGate()
	const navigate = useNavigate()
	const { orpc, isAuthReady } = useMarketplaceOrpc()

	const [statusFilter, setStatusFilter] = React.useState<string>(ALL)
	const [triggerFilter, setTriggerFilter] = React.useState<string>(ALL)
	const [evaluateOpen, setEvaluateOpen] = React.useState(false)

	const input = {
		status:
			statusFilter === ALL
				? undefined
				: (statusFilter as (typeof EVAL_JOB_STATUSES)[number]),
		trigger:
			triggerFilter === ALL
				? undefined
				: (triggerFilter as (typeof EVAL_JOB_TRIGGERS)[number]),
	}
	const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
		...orpc.cardScanner.listEvalJobs.queryOptions({ input }),
		enabled: allowed && isAuthReady,
		refetchInterval: (query) =>
			query.state.data?.jobs.some(
				(job) => !TERMINAL_JOB_STATUSES.includes(job.status),
			)
				? 10_000
				: false,
	})

	const suitesQuery = useQuery({
		...orpc.cardScanner.listEvalSuites.queryOptions({}),
		enabled: allowed && isAuthReady,
	})
	const suiteVersions = React.useMemo(() => {
		const map = new Map<string, number>()
		for (const entry of suitesQuery.data?.suites ?? []) {
			map.set(entry.suite.id, entry.suite.version)
		}
		return map
	}, [suitesQuery.data])

	const columnDefs = React.useMemo<ColDef<AdminEvalJob>[]>(
		() => [
			{
				headerName: "Targets",
				colId: "targets",
				minWidth: 220,
				sortable: false,
				valueGetter: (p) => (p.data ? describeTargets(p.data) : ""),
			},
			{
				headerName: "Suite",
				colId: "suite",
				minWidth: 90,
				valueGetter: (p) => {
					const version = p.data ? suiteVersions.get(p.data.suiteId) : undefined
					return version === undefined ? "" : `v${version}`
				},
			},
			{
				headerName: "Status",
				field: "status",
				minWidth: 150,
				cellRenderer: (p: ICellRendererParams<AdminEvalJob>) =>
					p.data ? <StatusBadge status={p.data.status} /> : null,
			},
			{
				headerName: "Trigger",
				field: "trigger",
				minWidth: 130,
				valueFormatter: (p) => String(p.value).replaceAll("_", " "),
			},
			{
				headerName: "Created",
				field: "createdAt",
				sort: "desc",
				minWidth: 140,
				cellRenderer: (p: ICellRendererParams<AdminEvalJob>) =>
					p.data ? <Instant iso={p.data.createdAt} /> : null,
			},
			{
				headerName: "Duration",
				colId: "duration",
				minWidth: 100,
				sortable: false,
				valueGetter: (p) =>
					p.data?.startedAt
						? formatDuration(p.data.startedAt, p.data.finishedAt)
						: "",
			},
			{
				headerName: "Heartbeat",
				colId: "heartbeat",
				minWidth: 130,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<AdminEvalJob>) =>
					p.data?.lastHeartbeatAt ? (
						<HeartbeatCell
							iso={p.data.lastHeartbeatAt}
							active={!TERMINAL_JOB_STATUSES.includes(p.data.status)}
						/>
					) : (
						<span className="text-muted-foreground">—</span>
					),
			},
			{
				headerName: "Triggered by",
				field: "triggeredBy",
				minWidth: 160,
			},
			{
				headerName: "Outcome",
				colId: "outcome",
				minWidth: 160,
				sortable: false,
				cellRenderer: (p: ICellRendererParams<AdminEvalJob>) => {
					if (!p.data) return null
					if (p.data.status === "done") {
						return <span className="text-muted-foreground">results stored</span>
					}
					if (p.data.status === "failed") {
						return (
							<span className="max-w-40 truncate text-bad">
								{p.data.failureReason ?? "failed"}
							</span>
						)
					}
					if (p.data.status === "queued") {
						return <span className="text-meta">waiting for an instance</span>
					}
					return <span className="text-meta">in flight</span>
				},
			},
		],
		[suiteVersions],
	)

	if (!allowed) return null

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Eval jobs"
				count={data?.jobs.length}
				description="One boot per job: a model set scored on a pinned suite"
				actions={
					<>
						<Button
							variant="outline"
							size="icon"
							aria-label="Refresh eval jobs"
							onClick={() => refetch()}
							disabled={isRefetching}
						>
							<RefreshCw className="h-4 w-4" />
						</Button>
						<Button onClick={() => setEvaluateOpen(true)}>
							<FlaskConical className="h-4 w-4" />
							Evaluate…
						</Button>
					</>
				}
			/>

			<Toolbar>
				<Select value={statusFilter} onValueChange={setStatusFilter}>
					<SelectTrigger className="w-48">
						<SelectValue placeholder="All statuses" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>All statuses</SelectItem>
						{EVAL_JOB_STATUSES.map((status) => (
							<SelectItem key={status} value={status}>
								{status.replaceAll("_", " ")}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select value={triggerFilter} onValueChange={setTriggerFilter}>
					<SelectTrigger className="w-48">
						<SelectValue placeholder="All triggers" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>All triggers</SelectItem>
						{EVAL_JOB_TRIGGERS.map((trigger) => (
							<SelectItem key={trigger} value={trigger}>
								{trigger.replaceAll("_", " ")}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</Toolbar>

			{isLoading ? (
				<Skeleton className="h-64 w-full" />
			) : isError ? (
				<QueryError what="eval jobs" error={error} />
			) : data?.jobs.length === 0 ? (
				<p className="text-muted-foreground">
					No eval jobs. Jobs appear when a training run ingests a model, when a
					suite is pinned, or when you dispatch one with Evaluate.
				</p>
			) : (
				<ScannerGrid<AdminEvalJob>
					columnDefs={columnDefs}
					rowData={data?.jobs}
					getRowId={(row) => row.id}
					onRowClicked={(row) =>
						navigate({
							to: "/card-scanner/eval-jobs/$jobId",
							params: { jobId: row.id },
						})
					}
					gridOptions={{ rowClass: "cursor-pointer" }}
				/>
			)}

			<EvaluateDialog
				open={evaluateOpen}
				onClose={() => setEvaluateOpen(false)}
			/>
		</div>
	)
}
